import { LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import { NetworkStateNotifier } from '../utility/notifications/network-state-notifier';

/**
 * Mixin that provides network state awareness to LitElement components.
 *
 * Features:
 * - Reactive `isOnline` property: the connection-health monitor's "not offline", i.e. the server
 *   answers (D-137). Until D-137 it followed the live-update connection, so a dropped stream read
 *   as offline; it no longer does.
 * - `wasOffline` flag to track if component experienced an offline period
 * - Optional `onNetworkOnline()` method called when the server answers again after being offline
 * - Optional `onNetworkOffline()` method called when going offline
 * - Optional `onLiveUpdatesResumed()` method called when the live-update connection is back after
 *   being down, for anything that must re-read what it may have missed while it was
 *   (after an outage the connection comes back too, so this fires then as well)
 *
 * Usage:
 * ```typescript
 * @customElement('my-component')
 * export class MyComponent extends WithNetworkState(LitElement) {
 *   // Override to refresh data on reconnection
 *   protected onNetworkOnline(): void {
 *     this.loadData();
 *   }
 * }
 * ```
 */
export const WithNetworkState = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- canonical TS/Lit mixin idiom: a mixin superclass constraint must accept arbitrary constructor args.
  T extends new (...args: any[]) => LitElement,
>(
  superClass: T
) => {
  class WithNetworkStateClass extends superClass {
    /**
     * Current network/SSE connection state.
     */
    @state()
    protected isOnline = false;

    /**
     * Tracks whether the component experienced an offline period.
     * Useful for determining if data might be stale.
     */
    protected wasOffline = false;

    /**
     * Unsubscribe function for the network state listener.
     */
    private _networkStateUnsubscribe?: () => void;

    connectedCallback(): void {
      super.connectedCallback();

      const notifier = NetworkStateNotifier.getInstance();

      // Initialize current state
      this.isOnline = notifier.online;
      let liveUpdatesConnected = notifier.liveUpdates === 'connected';

      // Register for updates
      this._networkStateUnsubscribe = notifier.onChange(() => {
        const newOnlineState = notifier.online;
        const wasOnline = this.isOnline;

        this.isOnline = newOnlineState;

        const nowConnected = notifier.liveUpdates === 'connected';
        if (nowConnected && !liveUpdatesConnected) {
          this.onLiveUpdatesResumed();
        }
        liveUpdatesConnected = nowConnected;

        if (!newOnlineState && wasOnline) {
          // Going offline
          this.wasOffline = true;
          this.onNetworkOffline();
        } else if (newOnlineState && !wasOnline) {
          // Coming back online
          this.onNetworkOnline();
        }
      });
    }

    disconnectedCallback(): void {
      super.disconnectedCallback();
      this._networkStateUnsubscribe?.();
      this._networkStateUnsubscribe = undefined;
    }

    /**
     * Called when the server answers again after the page was offline.
     * Override in subclass to retry work that needed the server.
     */
    protected onNetworkOnline(): void {
      // Override in subclass
    }

    /**
     * Called when the page goes offline (the server stopped answering).
     * Override in subclass to show offline state or pause operations.
     */
    protected onNetworkOffline(): void {
      // Override in subclass
    }

    /**
     * Called when the live-update connection is connected again after being down, whether or not
     * the page was ever offline. Override to refresh data whose updates may have been missed.
     */
    protected onLiveUpdatesResumed(): void {
      // Override in subclass
    }
  }

  return WithNetworkStateClass as unknown as Constructor<WithNetworkStateInterface> &
    T;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- canonical mixin Constructor helper; constructor args are necessarily arbitrary.
type Constructor<T> = new (...args: any[]) => T;

export interface WithNetworkStateInterface {
  /** Current network/SSE connection state (read-only from outside) */
  readonly isOnline: boolean;
  /** Whether component experienced offline period */
  readonly wasOffline: boolean;
}
